import React, { useState, useContext, FunctionComponent, useEffect, useMemo } from 'react';
import { Text } from '@mantine/core';
import { GraphQLClient } from '@pebble/common';
import CategoryFilter from 'components/SearchFilterOptions/CategoryFilter/CategoryFilter';
import ProviderFilter from 'components/SearchFilterOptions/ProviderFilter/ProviderFilter/ProviderFilter';
import SearchFilterOptions from 'components/SearchFilterOptions/SearchFilterOptions';
import TimeSelector from 'components/SearchFilterOptions/TimeSelector/TimeSelector';
import SuppliersQuery from 'graphql/queries/suppliers';
import { Category, FiltersQuery, SupplierFilterItem } from 'types';
import { TimeEnum } from 'enums';
import { useActivityFilters } from 'hooks/useActivityFilters';
import { Actions, trackAction } from 'utils/amplitude';
import { useRouter } from 'next/router';
import { handleSelection } from 'utils/filters/filters';

export const getSuppliers = async (): Promise<SupplierFilterItem[]> => {
  const { data } = await GraphQLClient.query({
    query: SuppliersQuery,
  });
  return data.marketplaceSuppliers;
};

export interface SearchFiltersProviderType {
  postcode?: string;
  categories?: Category[];
  defaultRadius?: string;
  children?: React.ReactNode;
}

export interface SearchFiltersContextType {
  currentInnerModalComponent: string | null;
  setCurrentInnerModalComponent: React.Dispatch<React.SetStateAction<string | null>>;
  showFiltersModal: boolean;
  setShowFiltersModal: React.Dispatch<React.SetStateAction<boolean>>;
  renderInnerModalComponent?: JSX.Element;
  categories: Category[];
  getModalTitle(): string;
  providers: SupplierFilterItem[];
  showProviderSearch: boolean;
  providerSearchString: string;
  setProviderSearchString: React.Dispatch<React.SetStateAction<string>>;
  // daysOfWeek: DaysOfWeek[];
  currentCategory: string;
  getCategoryLabel(): string;
  totalActivitiesCount: number;
  setTotalActivitiesCount: React.Dispatch<React.SetStateAction<number>>;
  selectedProviders: string[];
  getProvidersLabel(): string;
  distance?: string;
  postcode: string;
  setPostcode: React.Dispatch<React.SetStateAction<string>>;
  backButtonTrackEvent(): void;
  categoryLoader: boolean;
  setCategoryLoader: React.Dispatch<React.SetStateAction<boolean>>;
  activeTab: string | null;
  setActiveTab: React.Dispatch<React.SetStateAction<string | null>>;
  getTimeLabel: () => JSX.Element;
  selectedFilters: FiltersQuery;
  onFilterChange(filter: FiltersQuery): void;
  resetFilter(): void;
  onlineFilterIncluded: boolean;
  instantBookFilterIncluded: boolean;
  timeSelected: TimeEnum;
  seLat?: string;
  seLng?: string;
}

export const SearchFiltersContext = React.createContext<SearchFiltersContextType | undefined>(
  undefined,
);

export const SearchFiltersProvider: FunctionComponent<SearchFiltersProviderType> = ({
  children,
  postcode: initialPostcode,
  categories: categoryList,
  defaultRadius = '5',
}) => {
  const [currentInnerModalComponent, setCurrentInnerModalComponent] = useState<string | null>(null);
  const [postcode, setPostcode] = useState<string>(initialPostcode || '');
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [providers, setProviders] = useState<SupplierFilterItem[]>([]);
  const [providerSearchString, setProviderSearchString] = useState<string>('');
  const [totalActivitiesCount, setTotalActivitiesCount] = useState<number>(0);
  const [categoryLoader, setCategoryLoader] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('any_date');

  const { selectedFilters, onFilterChange, resetAllFilters, resetCategoryFilter, resetTimeFilter } =
    useActivityFilters(defaultRadius);

  const {
    distance,
    ageStart,
    ageEnd,
    time,
    dayOfTheWeek,
    startingFrom,
    category: currentCategory,
    suppliers,
    isOnline,
    location,
    seLat,
    seLng,
    isInstantBookOnly,
  } = selectedFilters;

  useEffect(() => {
    const urlPostcode = location?.split('-')[1];

    if (urlPostcode === 'map') {
      setPostcode('Map Area');
    }
  }, [location]);

  const timeSelected = useMemo(() => {
    if (!time) {
      return TimeEnum.ANY_TIME;
    }
    return time === TimeEnum.AM ? TimeEnum.AM : TimeEnum.PM;
  }, [time]);

  const categories = categoryList || [];

  const onlineFilterIncluded = isOnline === 'true';

  const instantBookFilterIncluded = isInstantBookOnly === 'true';

  const filtersApplied = useMemo(
    () =>
      Boolean(
        ageEnd ||
          ageStart ||
          time ||
          dayOfTheWeek ||
          startingFrom ||
          suppliers ||
          distance !== '5' ||
          currentCategory !== 'all-activities',
      ),
    [ageEnd, ageStart, currentCategory, dayOfTheWeek, distance, startingFrom, suppliers, time],
  );

  useEffect(() => {
    const setSupplierList = async () => {
      const supplierList = await getSuppliers();
      setProviders(supplierList);
    };
    setSupplierList();
  }, []);

  const selectedProviders = useMemo<string[]>(() => {
    if (selectedFilters.suppliers && Array.isArray(selectedFilters.suppliers)) {
      return selectedFilters.suppliers;
    }

    if (typeof selectedFilters.suppliers === 'string') {
      return [selectedFilters.suppliers];
    }

    return [];
  }, [selectedFilters]);

  const getModalTitle = () => {
    switch (currentInnerModalComponent) {
      case 'categories':
        return 'Activity';
      case 'time':
        return 'Time';
      case 'providers':
        return 'Provider';
      default:
        return 'Filter activities';
    }
  };

  const getCategoryLabel = () => {
    const selectedCategory = categories.find((category) => {
      return category.slug === currentCategory;
    });

    return selectedCategory?.name || '';
  };

  const getProvidersLabel = (): string => {
    if (selectedProviders.length === 0) {
      return 'All providers';
    }
    if (selectedProviders.length > 2) {
      const firstTwo = selectedProviders.slice(0, 2);
      const rest = selectedProviders.slice(2);
      const firstTwoNames = firstTwo
        .map((providerId) => {
          return providers.find((provider) => provider.id === providerId)?.name;
        })
        .join(', ');
      return `${firstTwoNames} and ${rest.length} more...`;
    }
    return selectedProviders
      .map((providerId) => {
        return providers.find((provider) => provider.id === providerId)?.name;
      })
      .join(', ');
  };

  const renderInnerModalComponent = useMemo(() => {
    switch (currentInnerModalComponent) {
      case 'categories':
        return <CategoryFilter />;
      case 'time':
        return <TimeSelector />;
      case 'providers':
        return <ProviderFilter />;
      default:
        return <SearchFilterOptions />;
    }
  }, [currentInnerModalComponent]);

  const showProviderSearch = useMemo(
    () => currentInnerModalComponent === 'providers',
    [currentInnerModalComponent],
  );

  const getTimeLabel = () => {
    return (
      <>
        <Text>{!time && 'Any time'}</Text>
        <Text>{time === TimeEnum.AM && 'Morning'}</Text>
        <Text>{time === TimeEnum.PM && 'Afternoon'}</Text>
      </>
    );
  };

  const backButtonTrackEvent = () => {
    switch (currentInnerModalComponent) {
      case 'categories':
        return trackAction(Actions.CATEGORY_BACK);
      case 'time':
        return trackAction(Actions.TIME_BACK);
      case 'providers':
        return trackAction(Actions.PROVIDER_BACK);
      default:
        return trackAction(Actions.FILTER_BACK);
    }
  };

  const router = useRouter();

  const resetFilter = (): void => {
    if (filtersApplied) {
      if (currentInnerModalComponent) {
        trackAction(Actions.FILTER_RESET, {
          screen: currentInnerModalComponent,
        });
      }

      if (currentInnerModalComponent === 'categories' && currentCategory !== 'all-activities') {
        setCategoryLoader(true);
        resetCategoryFilter();
      }

      if (currentInnerModalComponent === 'time' && time) {
        resetTimeFilter();
      }

      if (currentInnerModalComponent === 'providers' && suppliers) {
        handleSelection('', 'provider', onFilterChange);
      }

      if (currentInnerModalComponent === null) {
        trackAction(Actions.FILTER_RESET_ALL);

        setCategoryLoader(true);

        resetAllFilters();
      }
    }
    return;
  };

  useEffect(() => {
    setCategoryLoader(false);
  }, [router.query, setCategoryLoader]);

  const value: SearchFiltersContextType = {
    currentInnerModalComponent,
    setCurrentInnerModalComponent,
    showFiltersModal,
    setShowFiltersModal,
    renderInnerModalComponent,
    categories,
    getModalTitle,
    providers,
    showProviderSearch,
    providerSearchString,
    setProviderSearchString,
    getCategoryLabel,
    totalActivitiesCount,
    setTotalActivitiesCount,
    selectedProviders,
    getProvidersLabel,
    distance,
    postcode,
    setPostcode,
    backButtonTrackEvent,
    categoryLoader,
    setCategoryLoader,
    activeTab,
    setActiveTab,
    getTimeLabel,
    selectedFilters,
    onFilterChange,
    resetFilter,
    currentCategory: currentCategory || '',
    onlineFilterIncluded,
    instantBookFilterIncluded,
    timeSelected,
    seLat,
    seLng,
  };

  return <SearchFiltersContext.Provider value={value}>{children}</SearchFiltersContext.Provider>;
};

export const useSearchFiltersContext = (): SearchFiltersContextType => {
  const context = useContext(SearchFiltersContext);

  if (!context) {
    throw new Error('useSearchFiltersContext must be used within a SearchFiltersProvider');
  }

  return context;
};
